import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { acceptInviteSchema, type AcceptInviteFormData } from '@/schemas/auth';
import { useAuth } from '@/hooks/useAuth';
import { get } from '@/lib/api-client';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import type { InviteInfo } from '@/types/auth';
import type { ApiError } from '@/types/api';

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [validating, setValidating] = useState(true);
  const [invalidReason, setInvalidReason] = useState('');
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AcceptInviteFormData>({
    resolver: zodResolver(acceptInviteSchema),
  });

  useEffect(() => {
    if (!token) {
      setInvalidReason('Missing invitation token.');
      setValidating(false);
      return;
    }

    get<InviteInfo>(`/auth/invite/${token}`)
      .then((info) => {
        setInviteInfo(info);
      })
      .catch((err) => {
        setInvalidReason((err as ApiError).error || 'Invalid or expired invitation.');
      })
      .finally(() => {
        setValidating(false);
      });
  }, [token]);

  if (validating) {
    return (
      <AuthLayout title="Validating Invitation">
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </AuthLayout>
    );
  }

  if (invalidReason) {
    return (
      <AuthLayout title="Invalid Invitation" subtitle={invalidReason}>
        <div className="text-center">
          <Link to="/login" className="text-sm text-accent-primary hover:underline">
            Go to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const onSubmit = async (data: AcceptInviteFormData) => {
    setApiError('');
    try {
      await acceptInvite(token!, data.password, data.firstName, data.lastName);
      navigate('/', { replace: true });
    } catch (err) {
      setApiError((err as ApiError).error || 'Failed to create account');
    }
  };

  return (
    <AuthLayout
      title={`Join ${inviteInfo?.orgName}`}
      subtitle={`You've been invited as ${inviteInfo?.role}`}
    >
      {apiError && (
        <div role="alert" className="mb-4 rounded-md border border-accent-danger/30 bg-accent-danger/10 px-4 py-3 text-sm text-accent-danger">
          {apiError}
        </div>
      )}

      <div className="mb-4 rounded-md border border-border-default bg-bg-primary px-4 py-3">
        <p className="text-sm text-text-secondary">
          Email: <span className="font-medium text-text-primary">{inviteInfo?.email}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="First Name"
            autoComplete="given-name"
            error={errors.firstName?.message}
            {...register('firstName')}
          />
          <Input
            label="Last Name"
            autoComplete="family-name"
            error={errors.lastName?.message}
            {...register('lastName')}
          />
        </div>
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          helperText="Min 8 characters, 1 uppercase, 1 lowercase, 1 number"
          {...register('password')}
        />
        <Input
          label="Confirm Password"
          type="password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <Button type="submit" loading={isSubmitting} className="w-full">
          Create Account
        </Button>
      </form>
    </AuthLayout>
  );
}
